import { io, Socket } from 'socket.io-client';
import { IMessagingProvider, IMessage } from '../IMessagingProvider';
import axios from 'axios';
import { NotificationService } from '../../../utils/notifications';
import { encryptMessage, decryptMessage, getKeyPair } from '../../encryption';

// ── Stable device ID ─────────────────────────────────────────────────────────
// Generated once per browser and persisted in localStorage.
const DEVICE_ID_KEY = 'tapestry_device_id';
function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = 'dev_' + Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

async function registerDevice(publicKey: string): Promise<void> {
  const deviceId = getOrCreateDeviceId();
  const ua = navigator.userAgent;
  const isMobile = /Mobi|Android/i.test(ua);
  const isIOS    = /iPhone|iPad/i.test(ua);
  const deviceName = isIOS ? 'iPhone/iPad' : isMobile ? 'Mobile' : 'Desktop';
  try {
    await axios.post(`${API_BASE_URL}/api/devices/register`, {
      deviceId,
      deviceName,
      deviceType: isMobile ? 'mobile' : 'desktop',
      browser: ua.match(/(Chrome|Firefox|Safari|Edge)/)?.[1] || 'Unknown',
      publicKey,
    }, { withCredentials: true });
  } catch (e) {
    console.warn('[Device] registration failed:', e);
  }
}

// ── Public key history cache ──────────────────────────────────────────────────
// Stores every public key ever seen for a user so decryption works even after
// the other party regenerates their keypair.
const PK_CACHE_KEY = 'pk_history';

function getPKHistory(): Record<string, string[]> {
  try { return JSON.parse(localStorage.getItem(PK_CACHE_KEY) || '{}'); } catch { return {}; }
}

function addPKToHistory(userId: string, publicKey: string) {
  const h = getPKHistory();
  const existing = h[userId] || [];
  if (!existing.includes(publicKey)) {
    h[userId] = [publicKey, ...existing].slice(0, 10); // keep last 10 keys per user
    localStorage.setItem(PK_CACHE_KEY, JSON.stringify(h));
  }
}

function tryDecryptWithHistory(
  content: string,
  userId: string,
  currentKey: string | null,
  mySecretKey: string,
  history: Record<string, string[]> = getPKHistory()
): { text: string; mediaKey?: string; mediaMimeType?: string } | null {
  const candidates = [
    ...(currentKey ? [currentKey] : []),
    ...(history[userId] || []).filter(k => k !== currentKey),
  ];
  for (const pk of candidates) {
    try {
      const decrypted = decryptMessage(content, pk, mySecretKey);
      try {
        const parsed = JSON.parse(decrypted);
        return { text: parsed.text ?? decrypted, mediaKey: parsed.mediaKey, mediaMimeType: parsed.mediaMimeType };
      } catch {
        return { text: decrypted };
      }
    } catch { /* try next key */ }
  }
  return null;
}

// Use REACT_APP_API_BASE_URL env var, defaults to empty string (relative URLs)
// For dev: set REACT_APP_API_BASE_URL=http://localhost:8080 in .env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Push our public key to the server only when it differs from the last one we
// synced this session — it was being re-uploaded before every send and every
// conversation refresh, adding a round trip to each.
let lastSyncedPublicKey: string | null = null;
async function syncOwnPublicKey(): Promise<string> {
  const { initializeEncryption } = await import('../../encryption');
  const myPublicKey = initializeEncryption();
  if (myPublicKey !== lastSyncedPublicKey) {
    await axios.put(`${API_BASE_URL}/api/auth/user/public-key`, { publicKey: myPublicKey }, { withCredentials: true });
    lastSyncedPublicKey = myPublicKey;
  }
  return myPublicKey;
}

// Module-level singleton — lets VideoCallContext access the socket without a second connection
let _activeInstance: ServerMessagingProvider | null = null;
export function getActiveMessagingInstance(): ServerMessagingProvider | null {
  return _activeInstance;
}

// Server-side messaging provider - messages stored in MySQL database
export class ServerMessagingProvider implements IMessagingProvider {
  private socket: Socket | null = null;
  private currentUserId: string;
  private senderName: string = '';
  private messageCallbacks: Array<(msg: IMessage) => void> = [];
  private onlineStatusCallbacks: Array<(userId: string, isOnline: boolean) => void> = [];
  private typingCallbacks: Array<(userId: string, isTyping: boolean) => void> = [];
  private reactionCallbacks: Array<(messageId: string, userId: string, emoji: string, action: 'add' | 'remove') => void> = [];
  private readReceiptCallbacks: Array<(byUserId: string, messageIds: string[]) => void> = [];
  private onlineUsers: Set<string> = new Set();
  private _reviveIfDead: (() => void) | null = null;
  private _onVisibility: (() => void) | null = null;
  private _onBackfill: (() => void) | null = null;

  /** Called after a long backgrounding, once the socket reconnects — lets the UI re-fetch the active conversation to backfill missed messages. */
  onBackfillNeeded(callback: () => void): void {
    this._onBackfill = callback;
  }

  constructor(userId: string, senderName?: string) {
    this.currentUserId = userId;
    this.senderName = senderName || '';
  }

  onUserOnlineStatusChanged(callback: (userId: string, isOnline: boolean) => void): void {
    this.onlineStatusCallbacks.push(callback);
    // Replay the current set so subscribers that register AFTER the server's
    // initial `online-users` snapshot still see who's online. Without this, a
    // page refresh shows everyone offline until the next status flap.
    this.onlineUsers.forEach(uid => callback(uid, true));
  }

  async connect(): Promise<void> {
    // Connect to signaling server
    this.socket = io(API_BASE_URL, {
      withCredentials: true,
      transports: ['polling', 'websocket']
    });

    return new Promise((resolve, reject) => {
      if (!this.socket) return reject('Socket not initialized');

      // Register message/presence listeners ONCE — socket.io persists `.on()`
      // listeners across reconnects, so calling this again on every 'connect'
      // (including reconnects) would stack duplicate handlers and cause each
      // incoming message/notification to be processed multiple times.
      this.setupSocketListeners();

      this.socket.on('connect', async () => {
        this.socket!.emit('join', this.currentUserId);
        _activeInstance = this;
        // Register this device so recipients can encrypt message copies for it
        try {
          const { initializeEncryption } = await import('../../encryption');
          const myPublicKey = initializeEncryption();
          await registerDevice(myPublicKey);
        } catch (e) { /* non-fatal */ }
        resolve();
      });

      // On reconnect, re-announce presence so the server map stays accurate
      this.socket.on('reconnect', () => {
        this.socket!.emit('join', this.currentUserId);
      });

      this.socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        reject(error);
      });

      // Android PWA: revive socket if it silently died while backgrounded (Doze
      // mode) or while the network changed (wifi↔cellular). Conditional, so a
      // healthy socket isn't disturbed — no thrash on lock-screen / app-switch.
      this._reviveIfDead = () => {
        if (this.socket && !this.socket.connected) this.socket.connect();
      };
      let hiddenAt = 0;
      this._onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          hiddenAt = Date.now();
          return;
        }
        // Becoming visible again
        this._reviveIfDead?.();
        // socket.connected can report `true` for a "zombie" long-poll connection
        // that a reverse proxy (e.g. cPanel Passenger) silently dropped while the
        // tab was backgrounded — events sent during that window never arrive and
        // the UI looks "stuck" until something forces a fresh transport. If we
        // were hidden for more than ~10s, force a clean reconnect and re-fetch
        // the current conversation to backfill anything missed.
        const wasHiddenLong = hiddenAt && Date.now() - hiddenAt > 10_000;
        hiddenAt = 0;
        if (wasHiddenLong && this.socket) {
          this.socket.disconnect();
          this.socket.connect();
        }
        if (this._onBackfill) this._onBackfill();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('online', this._reviveIfDead);
    });
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Listen for real-time messages.
    // CRITICAL: never AWAIT a network fetch in here. Decrypt synchronously from
    // whatever key we have on hand (relay payload → local PK history); if
    // neither yields a decryption, dispatch the ciphertext copy and try to
    // recover it asynchronously without blocking the UI.
    this.socket.on('relayed-message', ({ fromUserId, encryptedMessage, senderName, senderPublicKey }) => {
      const keyPair = getKeyPair();
      const fromId = String(fromUserId);

      // Try every key we already have, in order: relay payload, then local
      // history (recent first). All synchronous — no network.
      const tryDecrypt = (pk: string): string | null => {
        try { return decryptMessage(encryptedMessage.content, pk, keyPair!.secretKey); }
        catch { return null; }
      };
      let decrypted: string | null = null;
      let usedPk: string | null = null;
      if (keyPair && encryptedMessage.encrypted && encryptedMessage.content) {
        const candidates = [
          ...(senderPublicKey ? [senderPublicKey as string] : []),
          ...(getPKHistory()[fromId] || []),
        ];
        for (const pk of candidates) {
          const out = tryDecrypt(pk);
          if (out !== null) { decrypted = out; usedPk = pk; break; }
        }
      }

      const baseMessage: IMessage = { ...encryptedMessage };
      const message: IMessage = decrypted !== null
        ? { ...encryptedMessage, content: decrypted, encrypted: false }
        : baseMessage;

      if (usedPk) addPKToHistory(fromId, usedPk);

      // Fire-and-forget browser notification — was previously awaited, which
      // also stalled message dispatch for slow notification permission checks.
      if (senderName) {
        NotificationService.showMessageNotification(senderName, message.content, fromUserId)
          .catch(() => {});
      }

      this.messageCallbacks.forEach(callback => callback(message));

      // If we couldn't decrypt with anything we had, fetch the sender's current
      // key in the BACKGROUND and re-emit the corrected message via callbacks.
      // Listeners that dedup by id (Redux addMessage does) will simply update
      // the existing row's content the next time the message arrives — but we
      // need an "update" path. The simplest: try decryption again with the new
      // key, and if it works, push a corrected message through.
      if (decrypted === null && keyPair && encryptedMessage.encrypted && fromId) {
        this.getUserPublicKey(fromId).then(pk => {
          if (!pk) return;
          const out = tryDecrypt(pk);
          if (out === null) return;
          addPKToHistory(fromId, pk);
          const fixed: IMessage = { ...encryptedMessage, content: out, encrypted: false };
          this.messageCallbacks.forEach(cb => cb(fixed));
        }).catch(() => {});
      }
    });

    // Initial snapshot of who is online when we join
    this.socket.on('online-users', (userIds: string[]) => {
      this.onlineUsers.clear();
      userIds.forEach(id => {
        if (id !== this.currentUserId) {
          this.onlineUsers.add(id);
          this.onlineStatusCallbacks.forEach(cb => cb(id, true));
        }
      });
    });

    // Incremental updates
    this.socket.on('user-online', (userId: string) => {
      this.onlineUsers.add(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, true));
    });

    this.socket.on('user-offline', (userId: string) => {
      this.onlineUsers.delete(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, false));
    });

    // Typing indicators from the other participant
    this.socket.on('typing', ({ fromUserId, isTyping }: { fromUserId: string; isTyping: boolean }) => {
      this.typingCallbacks.forEach(cb => cb(String(fromUserId), isTyping));
    });

    // Read receipts — the other participant read messages we sent
    this.socket.on('messages-read', ({ byUserId, messageIds }: { byUserId: string; messageIds: string[] }) => {
      if (!Array.isArray(messageIds)) return;
      this.readReceiptCallbacks.forEach(cb => cb(String(byUserId), messageIds));
    });

    // Reaction add/remove from the other participant
    this.socket.on('message-reaction', ({ messageId, userId, emoji, action }: { messageId: string; userId: string; emoji: string; action: 'add' | 'remove' }) => {
      this.reactionCallbacks.forEach(cb => cb(messageId, String(userId), emoji, action));
    });
  }

  onTypingChanged(callback: (userId: string, isTyping: boolean) => void): void {
    this.typingCallbacks.push(callback);
  }

  onReactionChanged(callback: (messageId: string, userId: string, emoji: string, action: 'add' | 'remove') => void): void {
    this.reactionCallbacks.push(callback);
  }

  /** Notify the recipient that we're typing (or stopped). Fire-and-forget over the socket. */
  sendTyping(recipientId: string, isTyping: boolean): void {
    this.socket?.emit('typing', { recipientId, isTyping });
  }

  /** Add or replace our reaction on a message, and notify the recipient. */
  async setReaction(messageId: string, recipientId: string, emoji: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/messages/${messageId}/reactions`, { emoji }, { withCredentials: true });
    this.socket?.emit('message-reaction', { recipientId, messageId, emoji, action: 'add' });
  }

  /** Remove our reaction from a message, and notify the recipient. */
  async removeReaction(messageId: string, recipientId: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/messages/${messageId}/reactions`, { withCredentials: true });
    this.socket?.emit('message-reaction', { recipientId, messageId, emoji: '', action: 'remove' });
  }

  /** Fetch when a user was last seen online (null if currently online or never recorded) */
  async getLastSeen(userId: string): Promise<number | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/user/${userId}/last-seen`, { withCredentials: true });
      const lastSeenAt = response.data?.lastSeenAt;
      if (!lastSeenAt) return null;
      return new Date(typeof lastSeenAt === 'string' ? lastSeenAt.replace(' ', 'T') : lastSeenAt).getTime();
    } catch {
      return null;
    }
  }

  async sendMessage(recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string; linkPreview?: any }): Promise<IMessage> {
    // Encrypt message before sending
    let encryptedContent = content;
    let isEncrypted = false;
    let senderPublicKey = '';

    try {
      // Ensure local keys exist and the server has our current key, while
      // fetching the recipient's public key in parallel (only encrypt if they
      // have one from their own device).
      const { initializeEncryption } = await import('../../encryption');
      const myPublicKey = initializeEncryption();
      let keyPair = getKeyPair();

      const [, recipientPublicKey] = await Promise.all([
        syncOwnPublicKey().catch(err => { console.warn('Failed to sync public key:', err); }),
        axios.get(`${API_BASE_URL}/api/auth/user/${recipientId}/public-key`, { withCredentials: true })
          .then(res => {
            const pk: string | null = res.data.publicKey || null;
            if (pk) addPKToHistory(recipientId, pk);
            return pk;
          })
          .catch(err => { console.warn('Failed to get recipient public key:', err); return null; })
      ]);

      if (keyPair && recipientPublicKey) {
        const payload = (mediaData?.mediaKey)
          ? JSON.stringify({ text: content, mediaKey: mediaData.mediaKey, mediaMimeType: mediaData.mediaMimeType })
          : content;
        encryptedContent = encryptMessage(payload, recipientPublicKey, keyPair.secretKey);
        isEncrypted = true;
        senderPublicKey = myPublicKey;
      }
    } catch (error) {
      console.warn('Encryption unavailable, sending as plain text:', error);
    }


    const message: IMessage = {
      id: this.generateMessageId(),
      senderId: this.currentUserId,
      recipientId,
      content,
      timestamp: Date.now(),
      status: 'sending',
      encrypted: isEncrypted,
      ...(mediaData && {
        mediaType: mediaData.mediaType as 'image' | 'video' | 'link' | 'audio',
        mediaUrl: mediaData.mediaUrl,
        thumbnailUrl: mediaData.thumbnailUrl,
        mediaKey: mediaData.mediaKey,
        mediaMimeType: mediaData.mediaMimeType,
        linkPreview: mediaData.linkPreview
      })
    };

    try {
      // Store on server with encrypted content
      const response = await axios.post(
        `${API_BASE_URL}/api/messages`,
        {
          id: message.id,
          recipientId,
          content: encryptedContent,
          isEncrypted,
          ...(mediaData && {
            mediaType: mediaData.mediaType,
            mediaUrl: mediaData.mediaUrl,
            thumbnailUrl: mediaData.thumbnailUrl,
            linkPreview: mediaData.linkPreview
          })
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        message.status = 'sent';

        // Relay via WebSocket for real-time delivery.
        // Include our public key so the recipient can decrypt without a
        // separate /public-key fetch on every incoming message (which was
        // silently being skipped, leaving incoming messages as raw ciphertext).
        if (this.socket && this.socket.connected) {
          this.socket.emit('relay-message', {
            recipientId,
            encryptedMessage: message,
            senderName: this.senderName,
            senderPublicKey: senderPublicKey || undefined
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }

    return message;
  }

  async getMessages(recipientId: string): Promise<IMessage[]> {
    try {
      // Re-sync our public key so the other user always has the current one
      try { await syncOwnPublicKey(); } catch (e) { /* non-fatal */ }

      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${recipientId}`,
        { params: { deviceId: getOrCreateDeviceId() }, withCredentials: true }
      );

      if (response.data.success) {
        const keyPair = getKeyPair();

        // Get sender's public key for decryption and add to history cache
        let senderPublicKey: string | null = null;
        try {
          const keyResponse = await axios.get(
            `${API_BASE_URL}/api/auth/user/${recipientId}/public-key`,
            { withCredentials: true }
          );
          if (keyResponse.data.success && keyResponse.data.publicKey) {
            senderPublicKey = keyResponse.data.publicKey;
            addPKToHistory(recipientId, senderPublicKey as string);
          }
        } catch (error) {
          console.warn('Failed to get sender public key:', error);
        }

        const pkHistory = getPKHistory(); // parse once, not once per message
        return response.data.data.messages.map((msg: any) => {
          let content = msg.content;
          let mediaKey: string | undefined;
          let mediaMimeType: string | undefined;

          if (msg.is_encrypted && keyPair) {
            // Determine which userId sent this message (for key lookup)
            const senderUserId = String(msg.sender_id) === recipientId ? recipientId : 'self';
            const lookupId = senderUserId === recipientId ? recipientId : recipientId;

            const result = tryDecryptWithHistory(msg.content, lookupId, senderPublicKey, keyPair.secretKey, pkHistory);
            if (result) {
              content = result.text;
              mediaKey = result.mediaKey;
              mediaMimeType = result.mediaMimeType;
            } else {
              content = '🔒 Older message (sent before encryption keys were refreshed)';
            }
          }

          return {
            id: msg.id,
            senderId: String(msg.sender_id),
            recipientId: String(msg.recipient_id),
            content,
            timestamp: new Date(
              typeof msg.created_at === 'string'
                ? msg.created_at.replace(' ', 'T')   // no 'Z' — MySQL DATETIME is server-local time
                : msg.created_at
            ).getTime(),
            status: msg.status,
            encrypted: false,
            ...(msg.expires_at && {
              expiresAt: new Date(
                typeof msg.expires_at === 'string'
                  ? msg.expires_at.replace(' ', 'T')
                  : msg.expires_at
              ).getTime()
            }),
            ...(msg.media_type && {
              mediaType: msg.media_type,
              mediaUrl: msg.media_url,
              thumbnailUrl: msg.thumbnail_url,
              mediaKey,
              mediaMimeType,
              linkPreview: msg.link_preview ? (typeof msg.link_preview === 'string' ? JSON.parse(msg.link_preview) : msg.link_preview) : undefined
            }),
            ...(msg.reactions && { reactions: msg.reactions })
          };
        });
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  }

  async getAllConversations(): Promise<{ userId: string; lastMessage: IMessage | null; unreadCount: number }[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversations`,
        { withCredentials: true }
      );

      if (response.data.success) {
        return response.data.data.conversations.map((conv: any) => ({
          userId: String(conv.user_id),
          lastMessage: null, // We can fetch this separately if needed
          unreadCount: conv.unread_count || 0
        }));
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      return [];
    }
  }

  async markAsRead(messageId: string): Promise<void> {
    await this.markManyAsRead([messageId]);
  }

  /** Marks messages read on the server; returns false if the request failed. */
  async markManyAsRead(messageIds: string[]): Promise<boolean> {
    try {
      await axios.put(
        `${API_BASE_URL}/api/messages/read`,
        { messageIds },
        { withCredentials: true }
      );
      return true;
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
      return false;
    }
  }

  /** Tell the sender, in real time, that we read their messages (updates their ✓✓). */
  sendReadReceipt(senderId: string, messageIds: string[]): void {
    this.socket?.emit('messages-read', { recipientId: senderId, messageIds });
  }

  onReadReceipt(callback: (byUserId: string, messageIds: string[]) => void): void {
    this.readReceiptCallbacks.push(callback);
  }

  async isOnline(userId: string): Promise<boolean> {
    return this.onlineUsers.has(userId);
  }

  async deleteConversation(userId: string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/messages/conversation/${userId}`,
        { withCredentials: true }
      );
    } catch (error: any) {
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      console.error('Failed to delete conversation — server says:', serverMsg, error);
      throw error;
    }
  }

  onMessageReceived(callback: (message: IMessage) => void): void {
    this.messageCallbacks.push(callback);
  }

  async disconnect(): Promise<void> {
    if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    if (this._reviveIfDead) window.removeEventListener('online', this._reviveIfDead);
    this._onVisibility = null;
    this._reviveIfDead = null;
    this._onBackfill = null;
    lastSyncedPublicKey = null; // next user/session re-syncs its key
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    if (_activeInstance === this) _activeInstance = null;
  }

  private generateMessageId(): string {
    return `${this.currentUserId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Fetches a user's current public key from the server so callers (e.g. message
  // edit) can re-encrypt content to that recipient with the same scheme used by
  // sendMessage. Also feeds the local PK history cache so future decrypts have
  // the key on hand if the user later rotates it.
  async getUserPublicKey(userId: string): Promise<string | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/user/${userId}/public-key`,
        { withCredentials: true }
      );
      const pk = response.data?.publicKey || null;
      if (pk) addPKToHistory(userId, pk);
      return pk;
    } catch (err) {
      console.warn('getUserPublicKey failed:', err);
      return null;
    }
  }

  getPublicKey(): string {
    return '';
  }
}
